import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {approveReport, Count, Discrepancy, getCount, ITER_NAMES, Iteration} from '../../utils'
import {webEvents} from '../../../common/WebEvents'
import {DataGrid, GridColDef} from '@mui/x-data-grid'
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from '@mui/material'
import {useDispatch} from 'react-redux'
import { createError } from '../../../store/slices/error'

type ReviewRowData = {
    id: string,
    sku: string;
    serial: string;
    expected: number;
    counted: number;
    diff: number;
    unitCost: string;
    totalVal: string;
}

type SummaryData = {
    tk: string | null,
    name: string,
    email: string | null,
    countId: number,
    iteration: string | null,
    totalValueGained: number,
    totalValueLost: number,
    totalQuantityGained: number,
    totalQuantityLost: number
}

function ReviewPage() {
    const {id} = useParams()
    const navigate = useNavigate()
    const [count, setCount] = useState<ReviewRowData[]>([])
    const [summaryData, setSummaryData] = useState<SummaryData>({
        tk: '',
        name: '',
        email: '',
        countId: 0,
        iteration: '',
        totalValueGained: 0,
        totalValueLost: 0,
        totalQuantityGained: 0,
        totalQuantityLost: 0
    })
    const [selectedRows, setSelectedRows] = useState<ReviewRowData[]>([])
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const dispatch = useDispatch()

    const cols: GridColDef[] = [
        {
            field: 'sku',
            headerName: 'SKU',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        },
        {
            field: 'serial',
            headerName: 'Serial',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        },
        {
            field: 'expected',
            headerName: 'Expected',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        },
        {
            field: 'counted',
            headerName: 'Counted',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        },
        {
            field: 'diff',
            headerName: 'Diff',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        },
        {
            field: 'unitCost',
            headerName: 'Unit Cost',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        },
        {
            field: 'totalVal',
            headerName: 'Total Value',
            flex: 1,
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>
        }
    ]

    const getName = (email: string | null) => {
        if (!email) return ''
        const username = email.split('@')[0].split('.')

        const firstName = username[0]
        const lastName = username.length > 1 ? username[1] : '' // Default to empty if no last name

        const capitalize = (name: string) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()

        return `${capitalize(firstName)} ${capitalize(lastName)}`.trim()
    }

    useEffect(() => {
        if (id) {
            setIsLoading(true)
            getCount(Number(id))
                .then((c: Count) => {
                    /* only one iteration can be in-review and it is always the last one */
                    let iter = c.iterations.pop() as Iteration

                    setSummaryData({
                        ...summaryData,
                        tk: c.locationId,
                        name: getName(iter.assignedTo),
                        email: iter.assignedTo,
                        countId: c.countId,
                        iteration: ITER_NAMES[iter.iterationId],
                        ...transformRowData(iter.discrepancies)
                    })
                })
                .catch(error => {
                    webEvents.error(
                        error.message,
                        {'review_page': 'Count being reviewed'},
                        error.stack
                    )
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }).finally(() => setIsLoading(false))
        }
    }, [])

    const transformRowData = (data: Discrepancy[]) => {
        let totalValueGained = 0
        let totalValueLost = 0
        let totalQtyGained = 0
        let totalQtyLost = 0

        const rows = data.map((item, index) => {
            const total = (item.counted - item.expected) * item.cost
            const difference = item.counted - item.expected
            if (total < 0) {
                totalValueLost += total
            } else if (total > 0) {
                totalValueGained += total
            }
            if (difference > 0) {
                totalQtyGained += difference
            } else if (difference < 0) {
                totalQtyLost += difference
            }
            return {
                id: `${item.sku}_${index}`,
                sku: item.sku,
                serial: item.serials.length > 0 ? 'Yes' : 'No',
                expected: item.expected,
                counted: item.counted,
                diff: difference,
                unitCost: '$' + formatCurrency(item.cost),
                totalVal: '$' + formatCurrency(total)
            }
        })
        setCount(rows)
        return {
            totalValueGained: (Math.round(totalValueGained * 100 + Number.EPSILON) / 100),
            totalValueLost: (Math.round(totalValueLost * 100 + Number.EPSILON) / 100),
            totalQuantityGained: totalQtyGained,
            totalQuantityLost: totalQtyLost
        }
    }

    const handleApproveReport = () => {
        const approvedRows = selectedRows.map((row) => {
            const type = row.serial === 'Yes' ?
                row.expected < row.counted ? 'unexpected' : 'missing'
                : null

            return {
                sku: row.sku,
                type
            }
        })
        if (id) {
            approveReport(parseInt(id), approvedRows)
                .then(() => {
                    handleClose()
                    navigate('/inventory/counts/review', {state: {submissionSuccess: true}, replace: true})
                }).catch(error => {
                webEvents.error(
                    error.message,
                    {countReview: id},
                    error.stack
                )
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
        }
    }

    const formatCurrency = (value: number): string => {
        return value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
    }

    const confirmSubmit = () => {
        setOpenConfirmDialog(true)
    }

    const handleClose = () => {
        setOpenConfirmDialog(false)
    }

    return (
        <>
            <Dialog
                open={openConfirmDialog}
                onClose={handleClose}>
                <DialogTitle>
                    Confirm Submission
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        You are about to submit your approval. Please confirm if you wish to
                        continue.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                    <Button onClick={handleClose} color="error">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApproveReport}
                        color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Box className="bg-gray-100 w-full flex flex-col items-center p-4">
                <Box className="mb-6" />
                <Typography variant="h4" component="h1" className="font-bold mb-4 text-center">
                    Review Iteration
                </Typography>
                {/* Summary Section */}
                <Backdrop open={isLoading} className="z-50">
                    <CircularProgress color="inherit" />
                </Backdrop>
                {count.length > 0 ? <>
                    <Box
                        className="bg-white p-4 rounded-lg shadow-lg mb-6 w-full max-w-6xl mx-auto text-left">
                        <Box className="flex flex-wrap justify-between mb-4">
                            <Typography className="text-gray-700 mb-2">
                                Truck (TK): <span className="font-semibold">{summaryData.tk}</span>
                            </Typography>
                            <Typography className="text-gray-700 mb-2">
                                Technician Name: <span className="font-semibold">{summaryData.name}</span>
                            </Typography>
                            <Typography className="text-gray-700 mb-2">
                                Email: <span className="font-semibold">{summaryData.email}</span>
                            </Typography>
                            <Typography className="text-gray-700 mb-2">
                                Count ID: <span className="font-semibold">{summaryData.countId}</span>
                            </Typography>
                            <Typography className="text-gray-700 mb-2">
                                Iteration: <span className="font-semibold">{summaryData.iteration}</span>
                            </Typography>
                        </Box>

                        <hr className="border-t border-gray-300 mb-4" />
                        {/* Financial Summary */}
                        <Box className="flex justify-center">
                            <Typography className="font-bold text-gray-800 mb-1 mx-4 text-center pr-3">
                                Total Value Gained: <span
                                className="font-semibold text-green-600">${formatCurrency(summaryData.totalValueGained)}</span>
                            </Typography>
                            <Typography className="font-bold text-gray-800 mb-1 mx-4 text-center pr-3">
                                Total Quantity Gained: <span
                                className="font-semibold">{summaryData.totalQuantityGained}</span>
                            </Typography>
                            <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center">
                                Total Value Lost: <span
                                className="font-semibold text-red-600">${formatCurrency(summaryData.totalValueLost)}</span>
                            </Typography>
                            <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center pr-3">
                                Total Quantity Lost: <span
                                className="font-semibold">{summaryData.totalQuantityLost}</span>
                            </Typography>
                        </Box>
                    </Box><Box className="flex justify-center w-full max-w-6xl" style={{maxHeight: 600, width: '100%'}}>
                    <Box className="w-full">
                        <DataGrid
                            rows={count}
                            columns={cols}
                            checkboxSelection
                            onRowSelectionModelChange={(ids) => {
                                const selectedIDs = new Set(ids)
                                const selectedRows = count.filter((row) => selectedIDs.has(row.id)
                                )
                                setSelectedRows(selectedRows)
                            }} />
                    </Box>
                </Box></> : <Box className="text-2xl mt-4 mb-4">
                    <h3>No Data to display</h3>
                </Box>}

                {/* Approve Button & Back to Review Page Button */}
                <Box mt={4} display="flex" className="w-full max-w-6xl mx-auto" justifyContent="space-between">

                    <Button
                        onClick={() => navigate('/inventory/counts/review')}
                        variant="contained"
                        color="secondary"
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
                    >
                        Back to Summary Page
                    </Button>


                    <Button
                        onClick={() => confirmSubmit()}
                        variant="contained"
                        color="primary"
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
                        disabled={count.length === 0}
                    >
                        Approve
                    </Button>

                </Box>

            </Box>
        </>
    )
}

export default ReviewPage
